import { ArrowDown01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import { CalendarModal } from '@molecules/CalendarModal';
import { SessionCardStatus } from '@sunrise-ui/composites';
import type { WithResponsive } from '@sunrise-ui/primitives';
import {
  Button,
  Colors,
  TextReg,
  isAndroid,
  useDynamicStyles,
  useMediaQueries,
} from '@sunrise-ui/primitives';
import { DateTime } from 'luxon';
import type { PropsWithChildren } from 'react';
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { StyleSheet, View } from 'react-native';
import type { MarkedDates } from 'react-native-calendars/src/types';

type Props = {
  startDateTime: string; // ISO date string with full datetime info
  onDateSelect?: (date: string) => void;
  markedDates?: MarkedDates;
  minDate?: string;
  maxDate?: string;
  status?: SessionCardStatus;
  hideTime?: boolean; // Hide time display for events, show for sessions
};

export const SECTION_LIST_DATE_HEADER_HEIGHT = 52;

export function CardListDateHeader({
  startDateTime,
  onDateSelect,
  markedDates = {},
  minDate,
  maxDate,
  status,
  hideTime = false,
}: PropsWithChildren<Props>) {
  const { isMobile } = useMediaQueries();
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const styles = useDynamicStyles(createStyles, { status });
  const [isVisible, setIsVisible] = useState(false);
  const [, forceUpdate] = useState(0);

  const buttonRef = useRef<View>(null);

  useLayoutEffect(() => {
    if (isVisible) setIsVisible(false);
  }, [isMobile]);

  const openCalendar = () => {
    if (buttonRef.current) {
      buttonRef.current.measureInWindow((x, y, _width, height) => {
        setMenuPosition({
          top: y + height + 12 + (isAndroid ? 20 : 0),
          left: x - (isMobile ? 70 : 130),
        });
        setIsVisible(true);
      });
    }
  };

  const closeCalendar = () => {
    setIsVisible(false);
  };

  const handleDayPress = (day: { dateString: string }) => {
    onDateSelect?.(day.dateString);
    closeCalendar();
  };

  const today = DateTime.now().toISODate();
  const sessionDateTime = DateTime.fromISO(startDateTime) as DateTime<true>;
  const dateOnly = sessionDateTime.toISODate();
  const timeOnly = sessionDateTime.toFormat('h:mm a');

  const finalMarkedDates = useMemo(
    () => ({
      ...markedDates,
      [dateOnly ?? today]: {
        ...markedDates[dateOnly ?? today],
        selected: true,
        selectedColor: Colors.purple1,
      },
    }),
    [markedDates, dateOnly, today],
  );

  const statusText =
    status === SessionCardStatus.NOW
      ? 'Now'
      : status === SessionCardStatus.NEXT
        ? 'Next'
        : '';

  // Force update when startDateTime arrives if it's in the future
  useEffect(() => {
    const sessionDateTime = DateTime.fromISO(startDateTime);
    const now = DateTime.now();
    const msUntilStart = sessionDateTime.diff(now).toMillis();

    if (msUntilStart > 0) {
      const timeoutId = setTimeout(() => {
        forceUpdate((prev) => prev + 1);
      }, msUntilStart);

      return () => clearTimeout(timeoutId);
    }
  }, [startDateTime]);

  return (
    <View style={styles.container}>
      <View style={styles.infoContainer}>
        <View style={styles.line} />
        {status && (
          <View style={styles.statusContainer}>
            <TextReg style={styles.timeText}>{statusText}</TextReg>
          </View>
        )}
      </View>
      <View style={styles.buttonContainer}>
        <Button
          ref={buttonRef}
          style={styles.button}
          labelStyle={styles.buttonLabel}
          onPress={openCalendar}
          size="small"
        >
          {sessionDateTime.toLocaleString({
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            weekday: 'short',
          })}
        </Button>
        <View style={styles.buttonIcon}>
          <HugeiconsIcon icon={ArrowDown01Icon} size={16} color={Colors.text} />
        </View>
      </View>

      {hideTime ? (
        <View style={styles.infoContainer}>
          <View style={styles.line} />
        </View>
      ) : (
        <View style={styles.infoContainer}>
          <View style={styles.timeContainer}>
            <TextReg style={styles.timeText}>{timeOnly}</TextReg>
          </View>
          <View style={styles.line} />
        </View>
      )}

      <CalendarModal
        isVisible={isVisible}
        onClose={closeCalendar}
        onDayPress={handleDayPress}
        initialDate={dateOnly}
        minDate={minDate}
        markedDates={finalMarkedDates}
        position={menuPosition}
        maxDate={maxDate}
      />
    </View>
  );
}

const createStyles = ({ isMobile, status }: WithResponsive<Props>) =>
  StyleSheet.create({
    container: {
      height: SECTION_LIST_DATE_HEADER_HEIGHT,
      paddingHorizontal: isMobile ? 12 : 20,
      backgroundColor: Colors.purple0,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
    },
    infoContainer: {
      alignItems: 'center',
      flexDirection: 'row',
      gap: 12,
      flex: 1,
    },
    buttonContainer: {
      position: 'relative',
      alignItems: 'center',
      flexDirection: 'row',
      gap: 8,
    },
    button: {
      borderWidth: 1,
      borderColor: Colors.opacity20,
      backgroundColor: Colors.purple0,
    },
    buttonLabel: {
      marginRight: 0,
      marginLeft: 0,
      marginBottom: 0,
      marginTop: 0,
      paddingHorizontal: 12,
      paddingRight: 30,
    },
    buttonIcon: {
      position: 'absolute',
      pointerEvents: 'none',
      right: 8,
      height: 30,
      justifyContent: 'center',
    },
    dateText: {
      fontSize: 16,
    },
    line: {
      height: 1,
      flex: 1,
      backgroundColor: Colors.opacity20,
    },
    timeContainer: {
      height: 30,
      justifyContent: 'center',
      paddingHorizontal: 12,
      borderWidth: 1,
      borderColor: Colors.opacity20,
      borderRadius: 15,
    },
    statusContainer: {
      height: 30,
      justifyContent: 'center',
      paddingHorizontal: 12,
      backgroundColor:
        status === SessionCardStatus.NOW ? Colors.pink0 : Colors.purple1,
      borderRadius: 15,
    },
    timeText: {
      fontSize: 12,
    },
  });
